import React, { useState, useEffect, useRef } from 'react';
import { X, ThermometerSun, ThermometerSnowflake, Linkedin, Facebook, MessageCircle, Twitter, PlusCircle } from 'lucide-react';
import { Contact } from '../../types';
import GroupInput from '../groups/GroupInput';

interface EditContactModalProps {
  contact: Contact | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (updatedContact: Contact) => void;
  existingGroups: string[];
}

const EditContactModal: React.FC<EditContactModalProps> = ({
  contact,
  isOpen,
  onClose,
  onUpdate,
  existingGroups
}) => {
  // Initialize state with default structure first
  const [formData, setFormData] = useState<Omit<Contact, 'id'> | null>(null);
  const groupsModifiedRef = useRef<boolean>(false);
  const [showSocialMedia, setShowSocialMedia] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use effect to populate form when contact or isOpen changes
  useEffect(() => {
    if (isOpen && contact) {
      const contactGroups = Array.isArray(contact.groups) ? [...contact.groups] : [];
      setFormData({
        ...contact, // Spread existing contact data
        groups: contactGroups, // Ensure groups is an array
        birthday: contact.birthday ? new Date(contact.birthday).toISOString().split('T')[0] : '', // Format date correctly for input type="date"
        email: contact.email || '', // Ensure empty strings instead of undefined for controlled inputs
        phone: contact.phone || '',
        notes: contact.notes || '',
        linkedin: contact.linkedin || '',
        facebook: contact.facebook || '',
        telegram: contact.telegram || '',
        xcom: contact.xcom || '',
      });
      if (contact.linkedin || contact.facebook || contact.telegram || contact.xcom) {
        setShowSocialMedia(true);
      }
      groupsModifiedRef.current = false;
      setIsSubmitting(false);
      setError(null);
    } else if (!isOpen) {
      // Reset form data when modal closes
      setFormData(null);
      setShowSocialMedia(false);
    }
  }, [contact, isOpen]);

  const handleFormChange = (field: keyof Omit<Contact, 'id'>, value: any) => {
    setFormData(prev => prev ? { ...prev, [field]: value } : null);
    if (field === 'groups') {
      groupsModifiedRef.current = true;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData && contact) {
      // Basic validation (e.g., name is required)
      if (!formData.name.trim()) {
        setError("Contact name cannot be empty.");
        return;
      }
      setError(null);
      setIsSubmitting(true);

      // Prepare the final updated data
      const updatedData = {
        ...formData,
        id: contact.id, // Add the id back
        groups: Array.isArray(formData.groups) ? formData.groups : []
      };
      
      try {
        // Call the update function
        await onUpdate(updatedData);
        onClose(); // Close on success
      } catch (err) {
        console.error("Error updating contact:", err);
        setError("Failed to update contact. Please try again.");
        setIsSubmitting(false);
      }
    }
  };

  if (!isOpen || !formData) return null; // Render nothing if not open or data not loaded

  return (
    // Overlay Style
    <div className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50 p-4 transition-opacity duration-300 ease-out">
      {/* Modal Panel Style */}
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Style */}
        <div className="flex justify-between items-center p-4 sm:p-5 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-800">Edit Contact</h2>
          <button 
            onClick={onClose} 
            className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            disabled={isSubmitting}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-grow">
          <form onSubmit={handleSubmit} id="edit-contact-form" className="space-y-5">
            {/* Input Styling */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Birthday</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                value={formData.birthday}
                onChange={(e) => handleFormChange('birthday', e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Groups</label>
              <GroupInput
                selectedGroups={formData.groups}
                existingGroups={existingGroups}
                onChange={(groups) => handleFormChange('groups', groups)}
              />
            </div>

            {/* Warmth Button Styling */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Warmth Level</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleFormChange('warmth', 'warm')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150 border ${
                    formData.warmth === 'warm'
                      ? 'bg-tag-yellow-bg text-tag-yellow-text border-yellow-300 shadow-sm' // Active warmth
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300' // Inactive warmth
                  }`}
                  disabled={isSubmitting}
                >
                  <ThermometerSun size={16} /> Warm
                </button>
                <button
                  type="button"
                  onClick={() => handleFormChange('warmth', 'cold')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150 border ${
                    formData.warmth === 'cold'
                      ? 'bg-tag-blue-bg text-tag-blue-text border-blue-300 shadow-sm' // Active warmth
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300' // Inactive warmth
                  }`}
                  disabled={isSubmitting}
                >
                  <ThermometerSnowflake size={16} /> Cold
                </button>
              </div>
            </div>

            <div>
              <button
                type="button"
                onClick={() => setShowSocialMedia(!showSocialMedia)}
                className="text-sm text-brand-primary hover:text-indigo-700 flex items-center gap-1"
                disabled={isSubmitting}
              >
                 <PlusCircle size={16}/> {showSocialMedia ? 'Hide' : 'Edit'} Social Media Profiles
              </button>
              {showSocialMedia && (
                <div className="mt-3 space-y-3 border-t pt-4 border-gray-100">
                  {/* LinkedIn */}
                  <div className="flex items-center gap-2">
                    <Linkedin size={18} className="text-gray-400"/>
                    <input
                      type="url"
                      placeholder="LinkedIn Profile URL"
                      className="flex-grow px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                      value={formData.linkedin}
                      onChange={(e) => handleFormChange('linkedin', e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                  {/* Facebook */}
                  <div className="flex items-center gap-2">
                    <Facebook size={18} className="text-gray-400"/>
                    <input
                      type="url"
                      placeholder="Facebook Profile URL"
                      className="flex-grow px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                      value={formData.facebook}
                      onChange={(e) => handleFormChange('facebook', e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                  {/* Telegram */}
                  <div className="flex items-center gap-2">
                    <MessageCircle size={18} className="text-gray-400"/>
                    <input
                      type="text"
                      placeholder="Telegram Username or Link"
                      className="flex-grow px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                      value={formData.telegram}
                      onChange={(e) => handleFormChange('telegram', e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                  {/* X/Twitter */}
                  <div className="flex items-center gap-2">
                     <Twitter size={18} className="text-gray-400"/>
                     <input
                       type="url"
                       placeholder="X (Twitter) Profile URL"
                       className="flex-grow px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                       value={formData.xcom}
                       onChange={(e) => handleFormChange('xcom', e.target.value)}
                       disabled={isSubmitting}
                     />
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                placeholder="Add any relevant notes here..."
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                disabled={isSubmitting}
              ></textarea>
            </div>

            {/* Placeholder for form actions, actual buttons are in the footer */}
          </form>
        </div>

        {/* Footer Style */}
        <div className="flex justify-end items-center gap-3 p-4 bg-gray-50 border-t border-gray-200 flex-shrink-0">
          {/* Cancel Button: Secondary Style */}
          <button
            type="button"
            onClick={onClose}
            className="bg-white text-gray-700 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm font-medium"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          {/* Save Changes Button: Primary Style */}
          <button
            type="submit" 
            form="edit-contact-form" // Link button to the form
            className={`bg-brand-primary text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium shadow-sm ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditContactModal;